/**
 * useCreateApiKeyForm — state + validation + mutation for minting a gateway
 * API key. Rendering lives in CreateApiKeyDialog; this hook owns the rest so
 * each stays single-responsibility.
 */

import { useState } from 'react'

import { requestedScopes } from '@/components/settings/api-keys/apiKeyScopes'
import { type ApiKeyCreateResponse, useCreateApiKey } from '@/hooks/useApiKeys'

interface CreateApiKeyForm {
  name: string
  setName: (v: string) => void
  expiresInDays: string
  setExpiresInDays: (v: string) => void
  allowDataApi: boolean
  setAllowDataApi: (v: boolean) => void
  error: string | null
  clearError: () => void
  isPending: boolean
  submit: () => void
  reset: () => void
}

export function useCreateApiKeyForm(
  onCreated: (key: ApiKeyCreateResponse) => void,
): CreateApiKeyForm {
  const mutation = useCreateApiKey()
  const [name, setName] = useState('')
  const [expiresInDays, setExpiresInDays] = useState('')
  // Off by default: a key that only sends traffic should not also be able to
  // read the team's traces if it leaks (TRA-1217).
  const [allowDataApi, setAllowDataApi] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function reset(): void {
    setName('')
    setExpiresInDays('')
    setAllowDataApi(false)
    setError(null)
  }

  function submit(): void {
    const trimmed = name.trim()
    if (!trimmed) return
    setError(null)

    const parsedDays = expiresInDays.trim() ? Number(expiresInDays.trim()) : null
    if (parsedDays !== null && (!Number.isInteger(parsedDays) || parsedDays < 1)) {
      setError('Expiry must be a whole number of days (1 or more).')
      return
    }

    mutation.mutate(
      {
        data: {
          name: trimmed,
          expires_in_days: parsedDays,
          scopes: requestedScopes(allowDataApi),
        },
      },
      {
        onSuccess: (result) => {
          if (result.status === 201) {
            onCreated(result.data)
            reset()
          } else {
            setError('Failed to create key. Please try again.')
          }
        },
        onError: () => {
          setError('Failed to create key. Please try again.')
        },
      },
    )
  }

  return {
    name,
    setName,
    expiresInDays,
    setExpiresInDays,
    allowDataApi,
    setAllowDataApi,
    error,
    clearError: () => { setError(null) },
    isPending: mutation.isPending,
    submit,
    reset,
  }
}
